import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import type { Deck } from '@/lib/api';
import { DeckCard } from '@/components/DeckCard';
import { groupDecks, applyGroupDir, type GroupMode } from '@/lib/deck-grouping';
import { useRoutePrefs } from '@/hooks/useRoutePrefs';

export type { GroupMode };

// ─── Types ───────────────────────────────────────────────────────────────────

interface DeckGridProps {
  decks: Deck[];
  groupMode?: GroupMode;
  isOwner?: boolean;
  onDelete?: (id: string) => void;
  maxDecks?: number;
}

type SortDir = 'asc' | 'desc' | null;

// ─── Constants ───────────────────────────────────────────────────────────────

const GROUP_ICONS: Record<GroupMode, React.ReactNode> = {
  bracket: <span className="material-icons text-lg">shield</span>,
  color: <span className="material-icons text-lg">palette</span>,
  alpha: <span className="material-icons text-lg">sort_by_alpha</span>,
  recent: <span className="material-icons text-lg">schedule</span>,
  curve: <span className="material-icons text-lg">show_chart</span>,
};

const GROUP_LABELS: Record<GroupMode, string> = {
  bracket: 'Bracket',
  color: 'Color',
  alpha: 'A-Z',
  recent: 'Recent',
  curve: 'Curve',
};

// ─── Toolbar Button ──────────────────────────────────────────────────────────

function ToolbarButton({
  mode,
  currentMode,
  sortDir,
  onClick,
}: {
  mode: GroupMode;
  currentMode: GroupMode;
  sortDir: SortDir;
  onClick: (m: GroupMode) => void;
}) {
  const active = mode === currentMode;
  const dirIcon = active && sortDir !== null
    ? <span className="text-[10px] leading-none opacity-80">{sortDir === 'asc' ? '↑' : '↓'}</span>
    : null;

  return (
    <button
      onClick={() => onClick(mode)}
      title={`Sort by ${GROUP_LABELS[mode]}`}
      className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-pill border cursor-pointer text-base font-medium transition-[background,border-color,color] duration-150 outline-none ${
        active
          ? 'border-primary bg-surface-container-high text-primary'
          : 'border-outline bg-transparent text-on-surface-variant'
      }`}
    >
      <span className="text-md">{GROUP_ICONS[mode]}</span>
      {GROUP_LABELS[mode]}
      {dirIcon}
    </button>
  );
}

// ─── Component ───────────────────────────────────────────────────────────────

export function DeckGrid({ decks, groupMode: initialMode, isOwner, onDelete, maxDecks }: DeckGridProps) {
  const defaultMode: GroupMode = initialMode ?? 'recent';
  const { pathname } = useLocation();
  const [prefs, setPrefs] = useRoutePrefs(`${pathname}:grid`, {
    groupMode: defaultMode,
    sortDir: null as SortDir,
  });
  const { groupMode, sortDir } = prefs;

  function handleModeClick(mode: GroupMode) {
    if (mode !== groupMode) {
      // Clicking a different button: activate it ascending
      setPrefs({ groupMode: mode, sortDir: 'asc' });
    } else if (sortDir === null || sortDir === 'asc') {
      // First click on current mode (or already asc): go to desc
      setPrefs({ sortDir: sortDir === null ? 'asc' : 'desc' });
    } else {
      // Third state: deactivate → reset to default
      setPrefs({ groupMode: defaultMode, sortDir: null });
    }
  }

  // Apply maxDecks limit — always computed so hooks order is stable
  const limitedDecks = maxDecks ? decks.slice(0, maxDecks) : decks;
  const groups = useMemo(() => {
    const raw = groupDecks(limitedDecks, groupMode);
    return sortDir !== null ? applyGroupDir(raw, sortDir) : raw;
  }, [limitedDecks, groupMode, sortDir]);
  const showToolbar = decks.length > 4;

  if (limitedDecks.length === 0) {
    return (
      <div className="text-center px-spacing-lg py-[48px] text-on-surface-variant">
        <div className="mb-spacing-md opacity-40">
          <span className="material-icons text-[48px] text-on-surface-variant">library_books</span>
        </div>
        <div className="text-lg font-medium mb-1 text-on-surface">
          No decks found
        </div>
        <div className="text-base">
          Import a deck from Moxfield or paste a decklist to get started.
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Toolbar */}
      {showToolbar && (
        <div className="flex gap-1.5 mb-spacing-md flex-wrap">
          {(['bracket', 'color', 'alpha', 'recent', 'curve'] as GroupMode[]).map(m => (
            <ToolbarButton key={m} mode={m} currentMode={groupMode} sortDir={sortDir} onClick={handleModeClick} />
          ))}
        </div>
      )}

      {/* Groups */}
      <div className="flex flex-col gap-spacing-lg">
        {groups.map((group) => (
          <div key={group.header}>
            {/* Group header */}
            {(groups.length > 1 || showToolbar) && (
              <div className="flex items-center gap-spacing-sm mb-3 pb-2 border-b border-outline-variant">
                {group.badge}
                <span className="text-lg font-semibold text-on-surface">
                  {group.header}
                </span>
                <span className="text-base text-on-surface-variant">
                  ({group.decks.length} {group.decks.length === 1 ? 'deck' : 'decks'})
                </span>
              </div>
            )}

            {/* Cards grid */}
            <div className="deck-cards-grid grid grid-cols-[repeat(auto-fill,minmax(280px,1fr))] gap-4">
              {group.decks.map((deck) => (
                <DeckCard
                  key={deck.id}
                  deck={deck}
                  isOwner={isOwner}
                  onDelete={onDelete}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
